import { useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import styled, { css, keyframes } from 'styled-components';
import { Container, Em, Button, Arrow, Badge, BadgeDot } from '@/components/primitives';
import { navigated, $selectedReportId } from '@/model/router';
import { $reportSearchId } from '@/model/search';
import { drillDownOpened, modalOpened } from '@/model/modals';
import {
  formatFactValue,
  findingToDrillItem,
  footprintToDrillItem,
  buildReportMarkdown,
  downloadTextFile,
  RELATED_TYPE_LABELS,
  type FactItem,
  type FoundLink,
  type RelatedItem,
} from './report.utils';
import { toastSent } from '@/model/toasts';
import { $form } from '@/model/onboard';
import { queryFn } from '@/api';
import type { FootprintItem, Insight, Recommendation, ReportMeta, ScoreCard } from '@/api/mocks/lume';

type Pane = 'profile' | 'sources' | 'competencies' | 'related' | 'insights';

const fade = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

const View = styled.section`
  padding-bottom: ${({ theme }) => theme.spacing[14]};
  animation: ${fade} ${({ theme }) => theme.motion.duration.panel} ${({ theme }) => theme.motion.easing.out};
`;

const Head = styled.div`
  padding: ${({ theme }) => `${theme.spacing[12]} 0 ${theme.spacing[10]}`};
  background: radial-gradient(700px 320px at 90% 0%, ${({ theme }) => theme.fx.ambHaloSoft}, transparent 60%);
  border-bottom: 1px solid ${({ theme }) => theme.line.soft};
  margin-bottom: ${({ theme }) => theme.spacing[11]};
  position: relative;
`;

const MetaRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  flex-wrap: wrap;
`;

const HeadTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.displaySm};
  line-height: 1;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.xtight};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  max-width: 720px;
  font-variation-settings: 'opsz' 72;

  @media (max-width: 720px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.hLg};
  }
`;

const HeadLede = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
  color: ${({ theme }) => theme.ink[2]};
  max-width: 640px;
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const ArchetypeCard = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[5]};
  padding: ${({ theme }) =>
    `${theme.spacing[4]} ${theme.spacing[7]} ${theme.spacing[4]} ${theme.spacing[5]}`};
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.pill};
  box-shadow: ${({ theme }) => theme.shadows.sh1};
`;

const ArchOrb = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 30%,
    ${({ theme }) => theme.tone.halo.soft},
    ${({ theme }) => theme.tone.halo.base} 60%,
    ${({ theme }) => theme.tone.halo.deep}
  );
`;

const ArchLbl = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.ink[3]};
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const ArchName = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyXl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.snug};
`;

const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[1]};
  background: ${({ theme }) => theme.surface.deep};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.pill};
  margin-bottom: ${({ theme }) => theme.spacing[9]};
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabBtn = styled.button<{ $on?: boolean }>`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[5]}`};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  color: ${({ theme }) => theme.ink[3]};
  border-radius: ${({ theme }) => theme.radii.pill};
  transition:
    background ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};

  &:hover {
    color: ${({ theme }) => theme.ink[1]};
  }

  ${({ $on, theme }) =>
    $on &&
    css`
      background: ${theme.surface.elev};
      color: ${theme.ink[1]};
      box-shadow: ${theme.shadows.sh1};
    `}
`;

const Pane = styled.div`
  animation: ${fade} ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: ${({ theme }) => theme.spacing[7]};
  position: relative;
`;

const CardH = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${({ theme }) => theme.spacing[6]};

  h4 {
    font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
    font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
    text-transform: uppercase;
    color: ${({ theme }) => theme.ink[3]};
  }
  .val {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    font-size: ${({ theme }) => theme.typography.fontSizes.ui};
    color: ${({ theme }) => theme.ink[3]};
  }
`;

const Footprint = styled.div`
  > .fp-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${({ theme }) => `${theme.spacing[4]} 0`};
    border-bottom: 1px solid ${({ theme }) => theme.line.soft};
    cursor: pointer;
    transition: color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};
  }
  > .fp-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  > .fp-item:first-child {
    padding-top: 0;
  }
  > .fp-item:hover {
    color: ${({ theme }) => theme.tone.halo.base};
  }
`;

const FootprintItem = styled.button.attrs({ className: 'fp-item' })`
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font: inherit;
  color: inherit;
`;

const Src = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const SrcIcon = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${({ theme }) => theme.surface.deep};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-size: ${({ theme }) => theme.typography.fontSizes.uiSm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.ink[3]};
`;

const Mono = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  color: ${({ theme }) => theme.ink[3]};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Insight = styled.button<{ $accent?: boolean }>`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[6]} ${theme.spacing[6]} ${theme.spacing[5]}`};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.surface.elev};
  cursor: pointer;
  transition:
    transform ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out},
    border-color ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out},
    box-shadow ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out};

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.ink[4]};
    box-shadow: ${({ theme }) => theme.shadows.sh2};
  }

  ${({ $accent, theme }) =>
    $accent &&
    css`
      background: linear-gradient(180deg, ${theme.tone.halo.tint}, ${theme.surface.elev});
      border-color: ${theme.tone.halo.soft};
    `}

  .ix {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    font-size: ${({ theme }) => theme.typography.fontSizes.ui};
    color: ${({ theme }) => theme.tone.halo.base};
    margin-bottom: ${({ theme }) => theme.spacing[3]};
  }
  h5 {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
    font-size: ${({ theme }) => theme.typography.fontSizes.hXs};
    line-height: ${({ theme }) => theme.typography.lineHeights.snug};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.snug};
    margin-bottom: ${({ theme }) => theme.spacing[3]};
    font-variation-settings: 'opsz' 48;
  }
  h5 em {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    color: ${({ theme }) => theme.tone.halo.base};
  }
  p {
    font-size: ${({ theme }) => theme.typography.fontSizes.uiLg};
    line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
    color: ${({ theme }) => theme.ink[2]};
  }
`;

const Reco = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${({ theme }) => theme.spacing[5]};
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[6]}`};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.surface.elev};
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  h5 {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
    font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.snug};
    margin-bottom: ${({ theme }) => theme.spacing[1]};
    font-variation-settings: 'opsz' 36;
  }
  h5 em {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    color: ${({ theme }) => theme.tone.halo.base};
  }
  p {
    font-size: ${({ theme }) => theme.typography.fontSizes.ui};
    color: ${({ theme }) => theme.ink[3]};
    line-height: ${({ theme }) => theme.typography.lineHeights.body};
  }
`;

const Foot = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing[11]};
  padding-top: ${({ theme }) => theme.spacing[8]};
  border-top: 1px solid ${({ theme }) => theme.line.soft};
  flex-wrap: wrap;
`;

// Найденные факты — два блока: «значение» (тянется), «тип/источник + confidence»
// узким столбцом справа. Раньше grid был 1.2fr | 2fr | 48px и текст значения
// зажимался в правую узкую колонку — для длинных URL и email это давало
// 4–5 переносов строк.
const FactRow = styled.a<{ $clickable?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 160px;
  gap: ${({ theme }) => theme.spacing[5]};
  align-items: start;
  padding: ${({ theme }) => `${theme.spacing[4]} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.line.soft};
  text-decoration: none;
  color: inherit;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:hover {
    color: ${({ theme, $clickable }) => ($clickable ? theme.tone.halo.base : 'inherit')};
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const FactValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  color: ${({ theme }) => theme.ink[1]};
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: ${({ theme }) => theme.typography.lineHeights.snug};
`;

const FactMeta = styled.div`
  text-align: right;
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[3]};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  line-height: ${({ theme }) => theme.typography.lineHeights.snug};

  @media (max-width: 600px) {
    text-align: left;
  }
`;

const FactConfidence = styled.span`
  display: inline-block;
  margin-left: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.tone.halo.base};
`;

// Pill-chip рядом с фактом — даёт понять «как этот факт читается»: хорошо,
// нейтрально или плохо. Цифра score — внутреннее представление LLM, в UI
// её не показываем (пользователю нужен ответ, а не метрика). Стиль повторяет
// onboarding-ChipInput для консистентности «значимых меток» в продукте.
const SentimentChip = styled.span<{ $tone: 'aurora' | 'mist' | 'solar' }>`
  display: inline-flex;
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.radii.pill};
  border: 1px solid ${({ theme, $tone }) => theme.tone[$tone].soft};
  background: ${({ theme, $tone }) => theme.tone[$tone].tint};
  color: ${({ theme, $tone }) => theme.tone[$tone].deep};
  font-family: ${({ theme }) => theme.typography.fontFamilies.sans};
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  white-space: nowrap;
`;

type SentimentTone = 'aurora' | 'mist' | 'solar';

const sentimentTone = (score: number): SentimentTone =>
  score >= 0.3 ? 'aurora' : score <= -0.3 ? 'solar' : 'mist';

const sentimentLabel = (score: number): string =>
  score >= 0.3 ? 'хорошо' : score <= -0.3 ? 'плохо' : 'нейтрально';

// Confidence-line на табе профиля — единственная фраза «Мы уверены / скорее
// всего / сомневаемся что это вы». Заменяет прежний score-card 100/100 +
// бары + лейблы very_high — пользователю важна интерпретация, а не метрика.
const ConfidenceLine = styled.p<{ $tone: 'aurora' | 'mist' | 'solar' }>`
  margin: 0 0 ${({ theme }) => theme.spacing[8]} 0;
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  border-left: 3px solid ${({ theme, $tone }) => theme.tone[$tone].base};
  background: ${({ theme, $tone }) => theme.tone[$tone].tint};
  color: ${({ theme }) => theme.ink[1]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
  border-radius: ${({ theme }) => theme.radii.sm};
`;

const confidencePhrase = (value: number): { text: string; tone: 'aurora' | 'mist' | 'solar' } => {
  if (value >= 70) return { text: 'Мы уверены, что это вы.', tone: 'aurora' };
  if (value >= 40) return { text: 'Скорее всего, это вы.', tone: 'mist' };
  return { text: 'Сомневаемся, что это вы.', tone: 'solar' };
};

// Локализованные имена типов фактов для виджетов «цифрового следа».
// Не покрытый тип отображается как-есть (на случай новых типов в pipeline).
const FACT_TYPE_LABEL_RU: Record<string, string> = {
  email: 'email',
  phone: 'телефон',
  inn: 'ИНН',
  ogrn: 'ОГРН',
  ogrnip: 'ОГРНИП',
  birth_date: 'дата рождения',
  handle: 'никнеймы',
  social_profile: 'соцпрофили',
  online_account: 'онлайн-сервисы',
  website: 'сайты',
  alias: 'псевдонимы',
  employment: 'работа',
  education: 'образование',
  award: 'награды',
  address: 'адрес',
  publication: 'публикации',
  media_mention: 'упоминания в СМИ',
  court_case: 'суды',
  company_member: 'участие в компаниях',
};

const factTypeLabel = (type: string): string => FACT_TYPE_LABEL_RU[type] ?? type;

// Виджет-карточка типа фактов — внешне как Insight, но не button (раздел не
// кликабельный, drill-down переехал на отдельный таб «источники»).
const FactGroupCard = styled.div`
  padding: ${({ theme }) => `${theme.spacing[6]} ${theme.spacing[6]} ${theme.spacing[5]}`};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.surface.elev};
`;

const FactGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  > span {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
    font-size: ${({ theme }) => theme.typography.fontSizes.hXs};
    color: ${({ theme }) => theme.ink[1]};
  }
  > em {
    font-style: normal;
    font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
    font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
    color: ${({ theme }) => theme.ink[3]};
  }
`;

const FactGroupList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const FactGroupItem = styled.li`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSizes.body};
  color: ${({ theme }) => theme.ink[1]};
  word-break: break-word;
`;

const NarrativeBody = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.body};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
  color: ${({ theme }) => theme.ink[1]};
  max-width: ${({ theme }) => theme.widths.reading};

  h2 {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
    font-size: ${({ theme }) => theme.typography.fontSizes.hMd};
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
    margin: ${({ theme }) => `${theme.spacing[8]} 0 ${theme.spacing[4]}`};
    color: ${({ theme }) => theme.ink[1]};

    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
    font-size: ${({ theme }) => theme.typography.fontSizes.bodyXl};
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
    margin: ${({ theme }) => `${theme.spacing[6]} 0 ${theme.spacing[3]}`};
    color: ${({ theme }) => theme.ink[2]};
  }

  p {
    margin: ${({ theme }) => `0 0 ${theme.spacing[4]}`};
  }

  ul,
  ol {
    padding-left: 1.5em;
    margin: ${({ theme }) => `0 0 ${theme.spacing[4]}`};
  }

  li {
    margin-bottom: ${({ theme }) => theme.spacing[2]};
  }

  a {
    color: ${({ theme }) => theme.tone.halo.base};
    text-decoration: underline;
    text-underline-offset: 2px;
    overflow-wrap: anywhere;

    &:hover {
      color: ${({ theme }) => theme.tone.halo.deep};
    }
  }

  code {
    font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
    font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
    background: ${({ theme }) => theme.surface.deep};
    padding: 0.1em 0.3em;
    border-radius: 3px;
  }
`;

const InsightsSubLabel = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.ink[3]};
  margin: ${({ theme }) => `${theme.spacing[10]} 0 ${theme.spacing[5]}`};
`;

const TABS: { id: Pane; label: string }[] = [
  { id: 'profile', label: 'профиль' },
  { id: 'sources', label: 'источники' },
  { id: 'competencies', label: 'цифровой след' },
  { id: 'related', label: 'окружение' },
  { id: 'insights', label: 'инсайты' },
];

// staleTime ~1 час — отчёт мутабельный (новый search → новый report), но
// в пределах одной сессии просмотра пользователь не ждёт обновлений каждые
// 30 секунд (default queryClient — 30000 мс) и переключение табов не должно
// вызывать сетевые запросы. queryKey включает reportSearchId — для нового
// search'а ключ автоматически меняется → react-query выкатывает свежие
// данные без ручного invalidateQueries.
const REPORT_STALE_MS = 60 * 60 * 1000;

export const Report = () => {
  const [pane, setPane] = useState<Pane>('profile');
  const form = useUnit($form);
  const selectedReportId = useUnit($selectedReportId);
  const reportSearchId = useUnit($reportSearchId);

  // Cache-buster для queryKey: если пользователь открыл исторический отчёт
  // (selectedReportId !== null), используем его id; иначе — id последнего
  // завершённого search'а. Без этого queryKey [..., null] не менялся между
  // поисками и react-query отдавал старый отчёт после нового завершённого
  // search'а.
  const cacheKey = selectedReportId ?? reportSearchId ?? '__latest__';

  // Если открыт прошлый отчёт через ReportHistory — все запросы получают
  // `?id=`. Если null — backend отдаёт latest report (как раньше).
  const idQuery = selectedReportId ? { id: selectedReportId } : undefined;

  const meta = useQuery({
    queryKey: ['/api/lume/report', cacheKey],
    queryFn: queryFn<ReportMeta>({ path: '/api/lume/report', query: idQuery }),
    staleTime: REPORT_STALE_MS,
  });
  const score = useQuery({
    queryKey: ['/api/lume/report/score', cacheKey],
    queryFn: queryFn<ScoreCard>({ path: '/api/lume/report/score', query: idQuery }),
    staleTime: REPORT_STALE_MS,
  });
  // Полный footprint, findings и facts включены сразу — drill-down модалка
  // должна быть доступна с любого таба (например, клик по «ИСТОЧНИКИ 17»
  // на профиле). Раньше эти запросы были lazy per-tab, и такой клик
  // отдавал бы пустой список.
  const fullFootprint = useQuery({
    queryKey: ['/api/lume/report/footprint', 'full', cacheKey],
    queryFn: queryFn<FootprintItem[]>({
      path: '/api/lume/report/footprint',
      query: { scope: 'full', ...(idQuery ?? {}) },
    }),
    staleTime: REPORT_STALE_MS,
  });
  // insights / related загружаются eagerly (без enabled gate) — нужно
  // знать data.length чтобы решить рендерить ли tab в навигации. Пустые
  // табы прячем целиком (см. visibleTabs ниже), вместо «пока нет данных»
  // плейсхолдеров.
  const insights = useQuery({
    queryKey: ['/api/lume/report/insights', cacheKey],
    queryFn: queryFn<Insight[]>({ path: '/api/lume/report/insights', query: idQuery }),
    staleTime: REPORT_STALE_MS,
  });
  const recommendations = useQuery({
    queryKey: ['/api/lume/report/recommendations', cacheKey],
    queryFn: queryFn<Recommendation[]>({ path: '/api/lume/report/recommendations', query: idQuery }),
    staleTime: REPORT_STALE_MS,
    enabled: pane === 'insights',
  });
  const foundLinks = useQuery({
    queryKey: ['/api/lume/report/findings', cacheKey],
    queryFn: queryFn<FoundLink[]>({ path: '/api/lume/report/findings', query: idQuery }),
    staleTime: REPORT_STALE_MS,
  });
  const narrative = useQuery({
    queryKey: ['/api/lume/report/narrative', cacheKey],
    queryFn: queryFn<{ markdown: string }>({ path: '/api/lume/report/narrative', query: idQuery }),
    staleTime: REPORT_STALE_MS,
    enabled: pane === 'profile',
  });
  const facts = useQuery({
    queryKey: ['/api/lume/report/facts', cacheKey],
    queryFn: queryFn<FactItem[]>({ path: '/api/lume/report/facts', query: idQuery }),
    staleTime: REPORT_STALE_MS,
  });
  const related = useQuery({
    queryKey: ['/api/lume/report/related', cacheKey],
    queryFn: queryFn<RelatedItem[]>({ path: '/api/lume/report/related', query: idQuery }),
    staleTime: REPORT_STALE_MS,
  });

  const archetypeName = meta.data?.archetype.name ?? '...';
  const archetypeAccent = meta.data?.archetype.nameAccent ?? '';
  const titleHead = meta.data?.title.plain ?? '...';
  const titleAccent = meta.data?.title.accent ?? '';
  const titleTail = meta.data?.title.tail ?? '';
  const reportId = meta.data?.reportId ?? '...';
  const signals = meta.data?.signals ?? 0;

  const fullName = [form.first_name, form.last_name].filter((p) => p && p.trim()).join(' ');
  const lede =
    fullName && form.role
      ? `${fullName}, ваш цифровой голос точен и спокоен. Ниже — карта того, как вас читают, слышат и запоминают как ${form.role}.`
      : (meta.data?.defaultLede ?? '');

  const allFindings = useMemo(() => foundLinks.data ?? [], [foundLinks.data]);
  const allFootprint = useMemo(() => fullFootprint.data ?? [], [fullFootprint.data]);
  const allFacts = useMemo(() => facts.data ?? [], [facts.data]);

  // Группировка для виджет-сетки на табе «цифровой след»: один FactGroupCard
  // на тип факта (email/handle/ИНН/...). Сохраняем порядок появления типов
  // — важнее категории идут первыми за счёт order'а в response (бэк уже
  // отсортировал по confidence).
  const factGroups = useMemo(() => {
    const byType = new Map<string, FactItem[]>();
    for (const fact of allFacts) {
      const bucket = byType.get(fact.type);
      if (bucket) bucket.push(fact);
      else byType.set(fact.type, [fact]);
    }
    return Array.from(byType.entries()).map(([type, factsOfType]) => ({
      type,
      facts: factsOfType,
    }));
  }, [allFacts]);

  // Drill-down: клик по любой строке/карточке источника открывает модалку
  // со ссылками этого источника. id у footprint совпадает с findings.source
  // (бэк гарантирует — см. portfolio-to-lume.buildFootprint).
  const openSource = (it: FootprintItem) => {
    const items = allFindings.filter((l) => l.source === it.id).map(findingToDrillItem);
    drillDownOpened({
      title: it.name,
      subtitle: it.meta,
      items,
      searchable: items.length > 12,
      pageSize: items.length > 50 ? 50 : undefined,
      emptyMessage: foundLinks.isLoading
        ? 'Загружаем ссылки источника…'
        : 'У этого источника нет публичных ссылок в payload.',
    });
  };

  // Recommendation onClick — навигация по стабильному id рекомендации
  // (бэк buildBaseRecommendations выдаёт verify-uncertain / review-top /
  // enrich-input). Без маппинга кнопка «Открыть» молча показывала toast.
  const openRecommendation = (r: Recommendation) => {
    if (r.id === 'verify-uncertain') {
      const items = allFindings.filter((l) => l.kind === 'uncertain').map(findingToDrillItem);
      drillDownOpened({
        title: 'Сомнительные находки',
        subtitle: `${items.length} из ${allFindings.length}`,
        items,
        searchable: true,
        pageSize: 50,
        emptyMessage: foundLinks.isLoading
          ? 'Загружаем сомнительные находки…'
          : 'LLM ни в чём не сомневался — uncertain пуст.',
      });
      return;
    }
    if (r.id === 'review-top') {
      drillDownOpened({
        title: 'Топ-источники',
        subtitle: 'отсортированы по числу сигналов',
        items: allFootprint.map(footprintToDrillItem),
        searchable: allFootprint.length > 12,
        emptyMessage: fullFootprint.isLoading
          ? 'Загружаем топ-источники…'
          : 'Топ-источники пока не вычислены.',
      });
      return;
    }
    if (r.id === 'enrich-input') {
      navigated('onboard');
      return;
    }
    toastSent(r.toast);
  };

  const handleDownloadMarkdown = () => {
    const md = buildReportMarkdown({
      meta: meta.data,
      score: score.data,
      narrative: narrative.data,
      insights: insights.data ?? [],
      recommendations: recommendations.data ?? [],
      facts: allFacts,
      related: related.data ?? [],
      findings: allFindings,
      footprint: allFootprint,
      form,
      fullName,
      lede,
    });
    const safeId = (meta.data?.reportId ?? 'report').replace(/[^a-zA-Z0-9_-]+/g, '-');
    downloadTextFile(`lume-${safeId}.md`, md);
    toastSent('Markdown скачан');
  };

  return (
    <View>
      <Head>
        <Container>
          <MetaRow>
            <Badge $tone="halo">
              <BadgeDot $tone="halo" />
              Отчёт готов
            </Badge>
            <Badge>№ {reportId}</Badge>
            <Badge $tone="aurora">{signals} сигн.</Badge>
            <Badge $tone="mist">обновлён сегодня</Badge>
          </MetaRow>
          <HeadTitle>
            {titleHead} <Em>{titleAccent}</Em>
            {titleTail}
          </HeadTitle>
          <HeadLede>{lede}</HeadLede>
          <ArchetypeCard>
            <ArchOrb />
            <ArchLbl>{meta.data?.archetype.label ?? 'Архетип'}</ArchLbl>
            <ArchName>
              {archetypeName} <Em>{archetypeAccent}</Em>
            </ArchName>
          </ArchetypeCard>
        </Container>
      </Head>

      <Container>
        <Tabs>
          {TABS.filter((tab) => {
            // Прячем «окружение»/«инсайты» если pipeline ничего туда не положил —
            // вместо мок-плейсхолдеров «пока нет данных». Loading-состояние
            // (data === undefined) показываем как видимый таб, чтобы tab не
            // мигал при первом рендере. Скрываем только когда заведомо пусто.
            if (tab.id === 'related') return related.data == null || related.data.length > 0;
            if (tab.id === 'insights') return insights.data == null || insights.data.length > 0;
            return true;
          }).map((tab) => (
            <TabBtn key={tab.id} $on={pane === tab.id} onClick={() => setPane(tab.id)}>
              {tab.label}
            </TabBtn>
          ))}
        </Tabs>

        {pane === 'profile' && (
          <Pane>
            {score.data &&
              (() => {
                const phrase = confidencePhrase(score.data.value);
                return <ConfidenceLine $tone={phrase.tone}>{phrase.text}</ConfidenceLine>;
              })()}

            {narrative.data?.markdown && narrative.data.markdown.trim().length > 0 && (
              <Card>
                <CardH>
                  <h4>краткое содержание</h4>
                </CardH>
                <NarrativeBody>
                  <ReactMarkdown>{narrative.data.markdown}</ReactMarkdown>
                </NarrativeBody>
              </Card>
            )}

            <Card style={{ marginTop: '32px' }}>
              <CardH>
                <h4>найденные факты · {allFacts.length}</h4>
                <span className="val">
                  <em>confidence ≥ 0.5</em>
                </span>
              </CardH>
              <FactsList facts={allFacts} loading={facts.isLoading} />
            </Card>
          </Pane>
        )}

        {pane === 'sources' && (
          <Pane>
            <Card>
              <CardH>
                <h4>все источники · {fullFootprint.data?.length ?? 0}</h4>
                <span className="val">
                  <em>клик — список ссылок</em>
                </span>
              </CardH>
              <Footprint>
                {(fullFootprint.data ?? []).map((it) => (
                  <FootprintItem key={it.id} onClick={() => openSource(it)}>
                    <Src>
                      <SrcIcon>{it.ic}</SrcIcon>
                      {it.name}
                    </Src>
                    <Mono>{it.meta}</Mono>
                  </FootprintItem>
                ))}
              </Footprint>
            </Card>

            <FoundLinksCard
              loading={foundLinks.isLoading}
              links={allFindings}
              onOpenAll={() =>
                drillDownOpened({
                  title: 'Все найденные ссылки',
                  subtitle: `${allFindings.length}`,
                  items: allFindings.map(findingToDrillItem),
                  searchable: true,
                  pageSize: 50,
                  emptyMessage: 'Сигналы не найдены.',
                })
              }
            />
          </Pane>
        )}

        {pane === 'competencies' && (
          <Pane>
            <InsightsGrid>
              {factGroups.map((group) => (
                <FactGroupCard key={group.type}>
                  <FactGroupHeader>
                    <span>{factTypeLabel(group.type)}</span>
                    <em>{group.facts.length}</em>
                  </FactGroupHeader>
                  <FactGroupList>
                    {group.facts.map((fact) => (
                      <FactGroupItem key={fact.id}>
                        <span>{formatFactValue(fact.type, fact.value) || '—'}</span>
                        {fact.sentiment && (
                          <SentimentChip $tone={sentimentTone(fact.sentiment.score)}>
                            {sentimentLabel(fact.sentiment.score)}
                          </SentimentChip>
                        )}
                      </FactGroupItem>
                    ))}
                  </FactGroupList>
                </FactGroupCard>
              ))}
            </InsightsGrid>
          </Pane>
        )}

        {pane === 'related' && (
          <Pane>
            <Card>
              <CardH>
                <h4>окружение · {related.data?.length ?? 0}</h4>
                <span className="val">
                  <em>связные лица — соавторы, коллеги, родственники</em>
                </span>
              </CardH>
              <RelatedList items={related.data ?? []} loading={related.isLoading} />
            </Card>
          </Pane>
        )}

        {pane === 'insights' && (
          <Pane>
            <InsightsGrid>
              {(insights.data ?? []).map((ins) => (
                <Insight
                  key={ins.id}
                  $accent={ins.accent}
                  onClick={() => toastSent('Инсайт сохранён в заметки')}
                >
                  <div className="ix">{ins.ix}</div>
                  <h5 dangerouslySetInnerHTML={{ __html: ins.heading }} />
                  <p>{ins.text}</p>
                </Insight>
              ))}
            </InsightsGrid>
            <InsightsSubLabel>точки роста</InsightsSubLabel>
            {(recommendations.data ?? []).map((r) => (
              <Reco key={r.id}>
                <div>
                  <h5 dangerouslySetInnerHTML={{ __html: r.heading }} />
                  <p>{r.text}</p>
                </div>
                <Button $variant={r.intent} $size="sm" onClick={() => openRecommendation(r)}>
                  {r.cta}
                </Button>
              </Reco>
            ))}
          </Pane>
        )}

        <Foot>
          <Button $variant="ghost" onClick={() => navigated('landing')}>
            ← На главную
          </Button>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button $variant="ghost" onClick={() => navigated('history')}>
              История
            </Button>
            <Button $variant="ghost" onClick={handleDownloadMarkdown}>
              Скачать Markdown
            </Button>
            <Button $variant="ghost" onClick={() => modalOpened({ kind: 'share' })}>
              Поделиться
            </Button>
            <Button onClick={() => navigated('onboard')}>
              Обновить отчёт <Arrow>→</Arrow>
            </Button>
          </div>
        </Foot>
      </Container>
    </View>
  );
};

// --- Подкомпоненты вынесены, чтобы main render не разъезжался ----------------

const FOUND_LINKS_PAGE_SIZE = 25;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.surface.bg};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.sm};
  color: ${({ theme }) => theme.ink[1]};
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.tone.halo.base};
  }
  &::placeholder {
    color: ${({ theme }) => theme.ink[4]};
  }
`;

const ScrollArea = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.line.soft};
  border-radius: ${({ theme }) => theme.radii.sm};
  padding: ${({ theme }) => `0 ${theme.spacing[4]}`};
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[3]};
  flex-wrap: wrap;
`;

type FoundLinksCardProps = {
  loading: boolean;
  links: FoundLink[];
  onOpenAll: () => void;
};

const FoundLinksCard = ({ loading, links, onOpenAll }: FoundLinksCardProps) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return links;
    return links.filter((l) => {
      const hay = `${l.url} ${l.title ?? ''} ${l.source}`.toLowerCase();
      return hay.includes(q);
    });
  }, [links, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / FOUND_LINKS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const visible = filtered.slice(safePage * FOUND_LINKS_PAGE_SIZE, (safePage + 1) * FOUND_LINKS_PAGE_SIZE);

  return (
    <Card style={{ marginTop: 16 }}>
      <CardH>
        <h4>найденные ссылки · {filtered.length}</h4>
        <span className="val">
          <em>accepted и uncertain</em>
        </span>
      </CardH>
      <SearchInput
        type="text"
        placeholder="Фильтр по URL, источнику или заголовку…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setPage(0);
        }}
      />
      {loading && <p style={{ opacity: 0.6 }}>загрузка…</p>}
      {!loading && filtered.length === 0 && (
        <p style={{ opacity: 0.6 }}>
          {links.length === 0
            ? 'Пока нет публичных ссылок с подтверждённым попаданием.'
            : 'По запросу ничего не нашлось.'}
        </p>
      )}
      {filtered.length > 0 && (
        <ScrollArea>
          <Footprint>
            {visible.map((l) => (
              <FootprintItem
                key={l.id}
                as="a"
                href={l.url}
                target="_blank"
                rel="noreferrer noopener"
                style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
              >
                <Src style={{ minWidth: 0, flex: 1 }}>
                  <SrcIcon>↗</SrcIcon>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.title || l.url}
                  </span>
                </Src>
                <Mono>{l.source}</Mono>
              </FootprintItem>
            ))}
          </Footprint>
        </ScrollArea>
      )}
      {filtered.length > FOUND_LINKS_PAGE_SIZE && (
        <Pager>
          <span>
            {safePage * FOUND_LINKS_PAGE_SIZE + 1}–
            {Math.min((safePage + 1) * FOUND_LINKS_PAGE_SIZE, filtered.length)} / {filtered.length}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              $variant="ghost"
              $size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
            >
              ← Назад
            </Button>
            <Button
              $variant="ghost"
              $size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
            >
              Дальше →
            </Button>
            <Button $variant="ghost" $size="sm" onClick={onOpenAll}>
              В отдельном окне
            </Button>
          </div>
        </Pager>
      )}
    </Card>
  );
};

const FACTS_PREVIEW_LIMIT = 20;

const FactsList = ({ facts, loading }: { facts: FactItem[]; loading: boolean }) => {
  const [expanded, setExpanded] = useState(false);
  if (loading) return <p style={{ opacity: 0.6 }}>загрузка…</p>;
  if (facts.length === 0) return <p style={{ opacity: 0.6 }}>пока нет фактов с уверенностью выше 0.5</p>;
  const visible = expanded ? facts : facts.slice(0, FACTS_PREVIEW_LIMIT);
  return (
    <>
      <div>
        {visible.map((f) => {
          const value = formatFactValue(f.type, f.value) || '—';
          const conf = Math.round(f.confidence * 100);
          const meta = f.sourceName ? `${f.type} · ${f.sourceName}` : f.type;
          return (
            <FactRow
              key={f.id}
              as={f.sourceUrl ? 'a' : 'div'}
              href={f.sourceUrl || undefined}
              target={f.sourceUrl ? '_blank' : undefined}
              rel={f.sourceUrl ? 'noreferrer noopener' : undefined}
              $clickable={Boolean(f.sourceUrl)}
            >
              <FactValue>{value}</FactValue>
              <FactMeta>
                {meta}
                <FactConfidence>· {conf}%</FactConfidence>
                {f.sentiment && (
                  <SentimentChip $tone={sentimentTone(f.sentiment.score)}>
                    {sentimentLabel(f.sentiment.score)}
                  </SentimentChip>
                )}
              </FactMeta>
            </FactRow>
          );
        })}
      </div>
      {facts.length > FACTS_PREVIEW_LIMIT && (
        <Button $variant="ghost" onClick={() => setExpanded((v) => !v)} style={{ marginTop: 12 }}>
          {expanded ? '↑ Скрыть' : `↓ Показать ещё ${facts.length - FACTS_PREVIEW_LIMIT}`}
        </Button>
      )}
    </>
  );
};

// Плоский список связных лиц. Группировки по person-name намеренно нет:
// LLM-fact-extractor кладёт person-name в connection_* факты опционально,
// и попытка склеить «один человек = много фактов» даёт ложные связи. Когда
// promt'ы научатся стабильно проставлять person_name — добавим v2 с
// группировкой. Сейчас пользователю показывается «вот контакты людей,
// связанных с target'ом» как отдельный блок, и этого достаточно.
const RelatedList = ({ items, loading }: { items: RelatedItem[]; loading: boolean }) => {
  if (loading) return <p style={{ opacity: 0.6 }}>загрузка…</p>;
  if (items.length === 0)
    return (
      <p style={{ opacity: 0.6 }}>
        Связных лиц с явной связью с субъектом пока не найдено. Это нормально для неактивных в публичной сети
        профилей — окружение появится, когда поиск зацепит соавторов, коллег или со-учредителей.
      </p>
    );
  return (
    <div>
      {items.map((r) => {
        const label = RELATED_TYPE_LABELS[r.type] ?? r.type.replace('connection_', '');
        const conf = Math.round(r.confidence * 100);
        const meta = r.sourceName ? `${label} · ${r.sourceName}` : label;
        return (
          <FactRow
            key={r.id}
            as={r.sourceUrl ? 'a' : 'div'}
            href={r.sourceUrl || undefined}
            target={r.sourceUrl ? '_blank' : undefined}
            rel={r.sourceUrl ? 'noreferrer noopener' : undefined}
            $clickable={Boolean(r.sourceUrl)}
          >
            <FactValue>{r.value || '—'}</FactValue>
            <FactMeta>
              {meta}
              <FactConfidence>· {conf}%</FactConfidence>
            </FactMeta>
          </FactRow>
        );
      })}
    </div>
  );
};
